import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import SHOP from "@/config/shop";
import { useAuth } from "@/context/AuthContext";

export function LoginPage() {
  useSEO('Prihlaseni', `Prihlaste se do sveho uctu na ${SHOP.domain}`);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success('Prihlaseni uspesne'); window.location.href = '/'; }
    catch (error) { toast.error(error.response?.data?.detail || 'Chyba prihlaseni'); setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="login-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6 text-center">Prihlaseni</h1>
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">E-mail</label><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 border border-slate-300 rounded-sm px-4 focus:border-[#004E98] outline-none" data-testid="login-email" /></div>
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Heslo</label><input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="w-full h-11 border border-slate-300 rounded-sm px-4 focus:border-[#004E98] outline-none" data-testid="login-password" /></div>
        <button type="submit" disabled={loading} className="w-full bg-[#004E98] text-white hover:bg-[#003B73] disabled:opacity-50 rounded-sm px-6 py-3 font-medium uppercase tracking-wide text-sm flex items-center justify-center gap-2" data-testid="login-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />}Prihlasit se</button>
      </form>
      <p className="text-center mt-4 text-sm text-slate-500">Nemate ucet? <Link to="/registrace" className="text-[#004E98] hover:underline">Zaregistrujte se</Link></p>
    </div>
  );
}

export function RegisterPage() {
  useSEO('Registrace', `Vytvorte si ucet na ${SHOP.domain}`);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', firstname: '', lastname: '', company: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await register(formData); toast.success('Registrace uspesna'); window.location.href = '/'; }
    catch (error) { toast.error(error.response?.data?.detail || 'Chyba registrace'); setLoading(false); }
  };

  const inputCls = "w-full h-11 border border-slate-300 rounded-sm px-4 focus:border-[#004E98] outline-none";

  return (
    <div className="max-w-md mx-auto px-4 py-12" data-testid="register-page">
      <h1 className="text-2xl font-bold text-slate-800 mb-6 text-center">Registrace</h1>
      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-md p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Jmeno *</label><input type="text" required value={formData.firstname} onChange={(e) => setFormData({...formData, firstname: e.target.value})} className={inputCls} data-testid="register-firstname" /></div>
          <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Prijmeni *</label><input type="text" required value={formData.lastname} onChange={(e) => setFormData({...formData, lastname: e.target.value})} className={inputCls} data-testid="register-lastname" /></div>
        </div>
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Firma</label><input type="text" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className={inputCls} /></div>
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">E-mail *</label><input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className={inputCls} data-testid="register-email" /></div>
        <div><label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1 block">Heslo *</label><input type="password" required minLength={6} value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} className={inputCls} data-testid="register-password" /></div>
        <button type="submit" disabled={loading} className="w-full bg-[#004E98] text-white hover:bg-[#003B73] disabled:opacity-50 rounded-sm px-6 py-3 font-medium uppercase tracking-wide text-sm flex items-center justify-center gap-2" data-testid="register-submit">{loading && <Loader2 className="w-4 h-4 animate-spin" />}Zaregistrovat se</button>
      </form>
      <p className="text-center mt-4 text-sm text-slate-500">Mate ucet? <Link to="/prihlaseni" className="text-[#004E98] hover:underline">Prihlaste se</Link></p>
    </div>
  );
}
